import logoUrl from './Images/logo.jpg';

let logoImg: Promise<HTMLImageElement> | null = null;
/** The newspaper logo (src/Images/logo.jpg), loaded once. */
function loadLogo(): Promise<HTMLImageElement> {
  if (!logoImg) {
    logoImg = new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => {
        logoImg = null;
        reject(new Error('logo'));
      };
      i.src = logoUrl;
    });
  }
  return logoImg;
}

/**
 * Cuts the area (sx, sy, sw, sh) out of a page picture and puts the logo band above it, exactly once.
 * Every picture that is saved or shared goes through here, and nowhere else adds a logo.
 */
export interface BandAds {
  left?: string; // picture url shown at the left of the logo
  right?: string; // picture url shown at the right of the logo
}

async function loadPicture(url: string): Promise<HTMLImageElement | null> {
  try {
    // fetched as a blob so the canvas is never "tainted" by a picture from another address (e.g. backend on :8080)
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const obj = URL.createObjectURL(await res.blob());
    return await new Promise((resolve) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => resolve(null);
      i.src = obj;
    });
  } catch {
    return null;
  }
}

/** What goes around a shared picture besides the logo: the day's adverts, and the headlines of the two flash-news bars. */
export interface ShareExtras {
  ads?: BandAds;
  flashTop?: string[]; // headlines of the bar under the top menu
  flashBottom?: string[]; // headlines of the bar above the page controls
}

interface Options {
  logo: boolean; // false for page 1, which already carries the newspaper name
  extras?: ShareExtras;
  dateLabel?: string; // the long date, in a strip under the logo (clippings)
}

/** Splits the headlines into lines that fit the width, joined with diamonds. Returns at most maxLines lines. */
function wrapHeadlines(g: CanvasRenderingContext2D, items: string[], width: number, maxLines: number): string[] {
  const words = items.map((t) => t.trim()).filter(Boolean).join('  \u25C6  ').split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const next = cur ? cur + ' ' + w : w;
    if (cur && g.measureText(next).width > width) {
      lines.push(cur);
      cur = w;
    } else cur = next;
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = kept[maxLines - 1].replace(/\s*\S*$/, '') + ' \u2026';
    return kept;
  }
  return lines;
}

interface Strip {
  lines: string[];
  fs: number;
  lh: number;
  pad: number;
  h: number;
  label: string;
  labelW: number;
  bg: string;
  labelBg: string;
}

function planStrip(g: CanvasRenderingContext2D, items: string[] | undefined, sw: number, label: string, bg: string, labelBg: string): Strip | null {
  if (!items || !items.some((t) => t.trim())) return null;
  const fs = Math.max(12, Math.min(34, Math.round(sw * 0.026)));
  const lh = Math.round(fs * 1.3);
  const pad = Math.round(fs * 0.5);
  g.font = '700 ' + fs + 'px system-ui, "Segoe UI", Arial, sans-serif';
  const labelW = sw >= 260 ? Math.ceil(g.measureText(label).width) + pad * 2 : 0;
  const lines = wrapHeadlines(g, items, sw - labelW - pad * 2, 5);
  return { lines, fs, lh, pad, h: lines.length * lh + pad * 2, label, labelW, bg, labelBg };
}

function drawStrip(g: CanvasRenderingContext2D, st: Strip, y: number, sw: number) {
  g.fillStyle = st.bg;
  g.fillRect(0, y, sw, st.h);
  if (st.labelW) {
    g.fillStyle = st.labelBg;
    g.fillRect(0, y, st.labelW, st.h);
    g.fillStyle = '#fff';
    g.font = '800 ' + st.fs + 'px system-ui, "Segoe UI", Arial, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(st.label, st.labelW / 2, y + st.h / 2 + 1);
  }
  g.fillStyle = '#fff';
  g.font = '700 ' + st.fs + 'px system-ui, "Segoe UI", Arial, sans-serif';
  g.textAlign = 'left';
  g.textBaseline = 'middle';
  st.lines.forEach((ln, i) => g.fillText(ln, st.labelW + st.pad, y + st.pad + st.lh * (i + 0.5) + 1));
}

/** Draws pic resized to fill the whole box (nothing is cropped, no gaps), with the same gold border as on screen. */
function contain(g: CanvasRenderingContext2D, pic: HTMLImageElement, x: number, y: number, w: number, h: number) {
  const b = Math.max(2, Math.round(h * 0.03)); // border width
  g.drawImage(pic, x + b, y + b, w - 2 * b, h - 2 * b); // stretched to fill the whole frame
  frame(g, x, y, w, h, b);
}

/** The gold border round a part of the logo band. */
function frame(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, b: number) {
  g.strokeStyle = '#c9a227';
  g.lineWidth = b;
  g.strokeRect(x + b / 2, y + b / 2, w - b, h - b);
}

/**
 * THE one place that builds every picture that is saved or shared (a whole page or a cropped clipping):
 *   logo band (with the day's two adverts at its left and right) / date strip / top flash-news strip / the paper / bottom flash-news strip.
 */
export async function composePicture(img: HTMLImageElement, sx: number, sy: number, sw: number, sh: number, o: Options): Promise<Blob> {
  const ads = o.extras?.ads;
  const left = o.logo && ads?.left ? await loadPicture(ads.left) : null;
  const right = o.logo && ads?.right ? await loadPicture(ads.right) : null;
  const logo = o.logo ? await loadLogo().catch(() => null) : null;
  const aspect = logo ? logo.naturalWidth / logo.naturalHeight : 1;
  const withAds = !!(logo && (left || right));
  // with adverts the page width is shared by three equal parts (advert, logo, advert), each as high as the logo's part
  const gap = Math.max(2, Math.round(sw * 0.004));
  const part = Math.floor((sw - 2 * gap) / 3);
  const band = !logo ? 0 : withAds ? Math.max(48, Math.round(part / aspect)) : Math.round(Math.max(48, Math.min(sw / aspect, Math.max(90, sh * 0.6))));
  const dateStrip = o.dateLabel ? Math.max(26, Math.round(sw * 0.045)) : 0;

  const probe = document.createElement('canvas').getContext('2d');
  if (!probe) throw new Error('no canvas');
  // the FLASH and ADVERTISEMENT bars are NOT put on a shared picture or a clipping (they only show on the open page)
  const top = null as ReturnType<typeof planStrip> | null;
  const bottom = null as ReturnType<typeof planStrip> | null;
  void probe; void planStrip;

  const c = document.createElement('canvas');
  c.width = sw;
  c.height = band + dateStrip + (top?.h ?? 0) + sh + (bottom?.h ?? 0);
  const g = c.getContext('2d');
  if (!g) throw new Error('no canvas');
  g.fillStyle = '#fff';
  g.fillRect(0, 0, c.width, c.height);

  if (logo && band && withAds) {
    const xs = [0, part + gap, 2 * (part + gap)];
    const wLast = sw - xs[2];
    // middle: the logo on its own yellow and orange, filling its part
    const split = Math.round(band * 0.56);
    g.fillStyle = '#f8ee21';
    g.fillRect(xs[1], 0, part, split);
    g.fillStyle = '#ed8e18';
    g.fillRect(xs[1], split, part, band - split);
    const dh = Math.min(band, Math.round(part / aspect));
    g.drawImage(logo, 0, 0, logo.naturalWidth, logo.naturalHeight, xs[1], Math.round((band - dh) / 2), part, dh);
    frame(g, xs[1], 0, part, band, Math.max(2, Math.round(band * 0.03)));
    // sides: the advert fills its part; with no advert there, the logo's own edge colours carry on
    if (left) contain(g, left, xs[0], 0, part, band);
    else g.drawImage(logo, 0, 0, Math.round(logo.naturalWidth * 0.18), logo.naturalHeight, xs[0], 0, part, band);
    if (right) contain(g, right, xs[2], 0, wLast, band);
    else g.drawImage(logo, Math.round(logo.naturalWidth * 0.82), 0, Math.round(logo.naturalWidth * 0.18), logo.naturalHeight, xs[2], 0, wLast, band);
  } else if (logo && band) {
    // "cover": the logo is scaled until it fills the whole band, so no white space is left
    const scale = Math.max(sw / logo.naturalWidth, band / logo.naturalHeight);
    const vw = sw / scale;
    const vh = band / scale;
    g.drawImage(logo, (logo.naturalWidth - vw) / 2, (logo.naturalHeight - vh) / 2, vw, vh, 0, 0, sw, band);
  }

  let y = band;
  if (dateStrip && o.dateLabel) {
    g.fillStyle = '#fbf6e9';
    g.fillRect(0, y, sw, dateStrip);
    g.fillStyle = '#b3141a';
    g.fillRect(0, y, sw, Math.max(2, Math.round(dateStrip * 0.07)));
    g.fillStyle = '#1c1a17';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    let fs = Math.round(dateStrip * 0.56);
    g.font = '700 ' + fs + 'px Georgia, "Times New Roman", serif';
    while (g.measureText(o.dateLabel).width > sw - 16 && fs > 8) {
      fs -= 1;
      g.font = '700 ' + fs + 'px Georgia, "Times New Roman", serif';
    }
    g.fillText(o.dateLabel, sw / 2, y + dateStrip / 2 + 1);
    y += dateStrip;
  }
  if (top) {
    drawStrip(g, top, y, sw);
    y += top.h;
  }
  g.drawImage(img, sx, sy, sw, sh, 0, y, sw, sh);
  y += sh;
  if (bottom) drawStrip(g, bottom, y, sw);
  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('blob'))), 'image/jpeg', 0.92));
}

/** A whole page (or an area of it) with the logo band on top. */
export function clipWithLogo(img: HTMLImageElement, sx: number, sy: number, sw: number, sh: number, extras?: ShareExtras, dateLabel?: string): Promise<Blob> {
  return composePicture(img, sx, sy, sw, sh, { logo: true, extras, dateLabel });
}

/** The whole page picture as it is, with no logo (page 1 already has the newspaper name) - only the flash-news strips are added. */
export function plainPicture(img: HTMLImageElement, extras?: ShareExtras): Promise<Blob> {
  return composePicture(img, 0, 0, img.naturalWidth, img.naturalHeight, { logo: false, extras: extras && { flashTop: extras.flashTop, flashBottom: extras.flashBottom } });
}
